import type { CarDetail, MicroStep, SwitchList, SwitchListItem } from '../types/interchange'

/**
 * Switch list print view-model (Plan 26 Phase 1c, D7).
 *
 * Pure transformation SwitchList → document model. The same view-model
 * drives the React print sheet and could drive a future server renderer —
 * keep it free of DOM/framework concerns.
 *
 * Paper is not access-controlled: the model prints the minimum — no tenant
 * IDs, no EDI document references beyond the explicit provenance fields the
 * caller supplies, no solver internals.
 */

export interface PrintProvenance {
  /** EDI-origin orders: train symbol, inbound carrier, interchange track,
   *  document reference. Yard-authored orders: locomotive, crew, requester. */
  kind: 'EDI' | 'YARD'
  trainSymbol?: string
  inboundCarrier?: string
  interchangeTrack?: string
  ediDocReference?: string
  locomotiveMark?: string
  crewName?: string
  requestedBy?: string
}

export interface PrintStep {
  type: string
  label: string
  switchName?: string
  requiredSwitchState?: string
  /** Entered-track length (see `MicroStep.travelDistanceFeet`) — printed
   *  via `formatEnteredTrackFeet`, never as bare leg distance. */
  travelDistanceFeet?: number
}

export interface PrintCarRow {
  reportingMark: string
  carType?: string
  loadStatus?: string
  lengthFeet?: number
  grossWeightTons?: number
  hazmatClass?: string
  hazardous?: boolean
  /** Destination spot/door is item-level on the wire — copied onto the row. */
  destination?: string
}

export interface PrintPass {
  passNumber: number
  summary: string
  statusReason?: string
  /** WARN-severity findings — printed as sub-lines under the reason. */
  warnings: string[]
  blocked: boolean
  destinationLabel?: string
  steps: PrintStep[]
  cars: PrintCarRow[]
  /** Car IDs with no hydrated detail — rendered as ID-only rows. */
  unresolvedCarIds: string[]
}

export interface SwitchListPrintViewModel {
  documentTitle: string
  swlNumber?: string
  woNumber?: string
  /** Telemetry revision (generation_version) — null on unrevisioned previews. */
  revision?: number
  provenance: PrintProvenance
  metrics: { label: string; value: string }[]
  passes: PrintPass[]
  printedAt: string
  /** Short URL crews can use to check currency (story route). */
  verifyUrl?: string
}

export interface PrintContext {
  provenance?: Partial<PrintProvenance>
  /** Locomotive reporting mark + crew name for yard-authored sheets. */
  locomotiveMark?: string
  crewName?: string
  requestedBy?: string
  /** Work order UUID — builds the verify URL pointing at the story page. */
  workOrderId?: string
  /** Absolute base for verifyUrl (e.g. window.location.origin). */
  origin?: string
  printedAt?: Date
}

export function buildSwitchListPrintViewModel(
  switchList: SwitchList,
  ctx: PrintContext = {}
): SwitchListPrintViewModel {
  const passes = (switchList.items ?? []).map(toPrintPass)
  const provenance: PrintProvenance = {
    kind: ctx.provenance?.kind ?? 'YARD',
    trainSymbol: ctx.provenance?.trainSymbol,
    inboundCarrier: ctx.provenance?.inboundCarrier,
    interchangeTrack: ctx.provenance?.interchangeTrack,
    ediDocReference: ctx.provenance?.ediDocReference,
    locomotiveMark: ctx.locomotiveMark ?? ctx.provenance?.locomotiveMark,
    crewName: ctx.crewName ?? ctx.provenance?.crewName,
    requestedBy: ctx.requestedBy ?? ctx.provenance?.requestedBy,
  }
  const verifyUrl =
    ctx.origin && ctx.workOrderId
      ? `${ctx.origin}/execution/switch-jobs/${ctx.workOrderId}/story`
      : undefined

  return {
    documentTitle: `Switch List ${switchList.swlNumber ?? '(draft)'}`,
    swlNumber: switchList.swlNumber,
    woNumber: switchList.woNumber,
    revision: switchList.generationVersion,
    provenance,
    metrics: [
      { label: 'Est. time', value: `${switchList.estimatedTimeToCompleteMinutes ?? 0} min` },
      { label: 'Distance', value: `${fmtNum(switchList.totalDistanceFeet)} ft` },
      { label: 'Switch throws', value: String(switchList.switchThrowCount ?? 0) },
      { label: 'Couplings', value: String(switchList.couplingCount ?? 0) },
      { label: 'Reversals', value: String(switchList.reversalCount ?? 0) },
      { label: 'Fuel (modeled)', value: `${switchList.estimatedFuelBurnGallons ?? 0} gal` },
    ],
    passes,
    printedAt: (ctx.printedAt ?? new Date()).toISOString(),
    verifyUrl,
  }
}

function toPrintPass(item: SwitchListItem): PrintPass {
  const detailById = new Map((item.cars ?? []).map((c) => [c.railCarId, c]))
  const cars: PrintCarRow[] = (item.carIds ?? [])
    .map((id) => detailById.get(id))
    .filter((c): c is CarDetail => c != null)
    .map((c) => toPrintCarRow(c, item))
  const unresolvedCarIds = (item.carIds ?? []).filter((id) => !detailById.has(id))

  const destination = [item.targetSpotIdentifier, item.targetDoorNumber ? `door ${item.targetDoorNumber}` : null]
    .filter(Boolean)
    .join(' · ')

  return {
    passNumber: item.passNumber ?? 0,
    summary: item.passSummary ?? item.summary ?? '',
    statusReason: item.statusReason,
    warnings: item.warnings ?? [],
    // Structured status wins (F-SWL-3). Legacy payloads have no status
    // field — a WARN-only task carries "WARNING:…" in statusReason, so
    // the fallback must not treat warnings as a block.
    blocked:
      item.status === 'BLOCKED' ||
      (item.status == null &&
        item.statusReason != null &&
        !item.statusReason.startsWith('WARNING')),
    destinationLabel: destination || item.targetTrackName,
    steps: (item.microSteps ?? []).map(toPrintStep),
    cars,
    unresolvedCarIds,
  }
}

function toPrintCarRow(car: CarDetail, item: SwitchListItem): PrintCarRow {
  return {
    reportingMark: car.reportingMark ?? car.railCarId,
    carType: car.assetType,
    loadStatus: car.loadStatus,
    lengthFeet: car.lengthFeet,
    grossWeightTons: car.grossWeightTons,
    hazmatClass: car.hazmatClass,
    hazardous: car.hazardous,
    destination:
      item.targetSpotIdentifier ??
      (item.targetDoorNumber ? `door ${item.targetDoorNumber}` : item.targetTrackName),
  }
}

function toPrintStep(step: MicroStep): PrintStep {
  // TRAVEL renders "from → to" so identical-length legs stay unambiguous on
  // paper (1c-i). The backend also bakes this into `description`; derive from
  // the name fields when present and fall back to the description otherwise.
  const label =
    step.type === 'TRAVEL' && (step.fromTrackName || step.toTrackName)
      ? `TRAVEL ${step.fromTrackName ?? '?'} → ${step.toTrackName ?? '?'}`
      : (step.description ?? step.type)
  return {
    type: step.type,
    label,
    switchName: step.switchName,
    requiredSwitchState: step.requiredSwitchState,
    travelDistanceFeet: step.travelDistanceFeet,
  }
}

function fmtNum(v: number | undefined): string {
  return (v ?? 0).toLocaleString()
}
